import { hasPhotoSessionUrl } from './photoSession';
import type {
  CalendarPage,
  CalendarPhoto,
  CalendarProject,
  PreflightCheckId,
  PreflightCheckResult,
  PreflightIssue,
  PreflightReport,
  PreflightSeverity,
} from '../types/calendarProject';

const screenshotWords = ['screenshot', 'snímek', 'snimek', 'screen', 'obrazovky'];
const screenshotIssue = 'Vypadá jako screenshot – ověř tiskovou kvalitu.';
const aspectIssue = 'Neobvyklý poměr stran – může být nutný ruční ořez.';

type CheckDefinition = {
  id: PreflightCheckId;
  label: string;
  description: string;
};

const checkDefinitions: CheckDefinition[] = [
  { id: 'page_count', label: 'Počet stran', description: 'Kontrola titulky a 12 měsíčních stránek.' },
  { id: 'missing_photos', label: 'Chybějící fotky', description: 'Každá stránka musí mít přiřazenou fotografii.' },
  { id: 'photo_quality', label: 'Kvalita fotek', description: 'Kontrola skóre fotek pro tiskové použití.' },
  { id: 'screenshot_warning', label: 'Screenshoty', description: 'Varování u souborů, které vypadají jako screenshot.' },
  { id: 'unusual_aspect_ratio', label: 'Poměr stran', description: 'Detekce extrémně širokých nebo vysokých snímků.' },
  { id: 'crop_extreme', label: 'Extrémní ořez', description: 'Kontrola zoomu, pozice a rotace ořezu.' },
  { id: 'text_length', label: 'Délka textů', description: 'Kontrola délky popisků u stránek.' },
  { id: 'safe_zone', label: 'Bezpečná zóna', description: 'MVP kontrola dostupnosti crop dat pro bezpečnou zónu.' },
  { id: 'bleed', label: 'Spadávka', description: 'MVP kontrola dostupnosti layoutu pro spadávku.' },
  { id: 'local_assets', label: 'Lokální assety', description: 'Lokální originály jsou dostupné v tomto prohlížeči.' },
];

function getPhoto(project: CalendarProject, page: CalendarPage) {
  return project.photos.find((photo) => photo.id === page.photoId);
}

function pageLabel(page?: CalendarPage) {
  if (!page) return 'Neznámá stránka';
  return page.type === 'cover' ? 'Titulka' : `${page.title} ${page.year ?? ''}`.trim();
}

function hasScreenshotWarning(photo: CalendarPhoto) {
  const normalizedName = photo.fileName.toLocaleLowerCase('cs-CZ');
  return photo.issues.includes(screenshotIssue) || screenshotWords.some((word) => normalizedName.includes(word));
}

function hasUnusualAspectRatio(photo: CalendarPhoto) {
  const aspectRatio = photo.width && photo.height ? Math.max(photo.width / photo.height, photo.height / photo.width) : 0;
  return photo.issues.includes(aspectIssue) || aspectRatio > 2.5;
}

function issue(input: Omit<PreflightIssue, 'id'>): PreflightIssue {
  const parts = [input.checkId, input.pageId, input.photoId, input.title].filter(Boolean).join('_');
  return { ...input, id: parts.toLocaleLowerCase('cs-CZ').replace(/[^a-z0-9_]+/g, '-') };
}

function result(definition: CheckDefinition, passed: number, issues: PreflightIssue[]): PreflightCheckResult {
  const errors = issues.filter((item) => item.severity === 'error').length;
  const warnings = issues.filter((item) => item.severity === 'warning').length;
  const severity: PreflightSeverity = errors ? 'error' : warnings ? 'warning' : 'ok';

  return {
    ...definition,
    severity,
    passed,
    warnings,
    errors,
    issues,
  };
}

export function runPreflight(project: CalendarProject, availableLocalAssetPhotoIds?: Set<string>): PreflightReport {
  const checks: PreflightCheckResult[] = [];
  const definition = (id: PreflightCheckId) => checkDefinitions.find((item) => item.id === id)!;

  checks.push(result(definition('page_count'), project.pages.length === 13 ? 1 : 0, project.pages.length === 13 ? [] : [issue({
    checkId: 'page_count',
    severity: 'error',
    title: 'Neočekávaný počet stran',
    message: `Projekt má ${project.pages.length} stran, očekáváme 13 stran.`,
    recommendation: 'Vrať se do Magic Draftu a přegeneruj návrh titulky + 12 měsíců.',
  })]));

  const missingPhotoIssues = project.pages.filter((page) => !page.photoId || !getPhoto(project, page)).map((page) => issue({
    checkId: 'missing_photos',
    severity: 'error',
    pageId: page.id,
    photoId: page.photoId,
    title: 'Chybí fotka',
    message: page.photoId ? `${pageLabel(page)} odkazuje na fotku, kterou v projektu nelze najít.` : `${pageLabel(page)} nemá přiřazenou fotografii.`,
    recommendation: 'Otevři stránku v editoru a přiřaď fotografii z nahraných fotek.',
  }));
  checks.push(result(definition('missing_photos'), project.pages.length - missingPhotoIssues.length, missingPhotoIssues));

  const photoQualityIssues = project.pages.flatMap((page) => {
    const photo = getPhoto(project, page);
    if (!photo) return [];
    if (photo.score >= 75) return [];
    return [issue({
      checkId: 'photo_quality',
      severity: photo.score >= 45 ? 'warning' : 'error',
      pageId: page.id,
      photoId: photo.id,
      title: photo.score >= 45 ? 'Nižší kvalita fotky' : 'Nevhodná kvalita fotky',
      message: `${pageLabel(page)} používá fotku se skóre ${photo.score}.`,
      recommendation: photo.score >= 45 ? 'Zvaž výměnu fotky nebo jemnější ořez.' : 'Nahraď fotku kvalitnějším snímkem pro tisk.',
    })];
  });
  checks.push(result(definition('photo_quality'), project.pages.filter((page) => {
    const photo = getPhoto(project, page);
    return photo ? photo.score >= 75 : false;
  }).length, photoQualityIssues));

  const screenshotIssues = project.pages.flatMap((page) => {
    const photo = getPhoto(project, page);
    if (!photo || !hasScreenshotWarning(photo)) return [];
    return [issue({
      checkId: 'screenshot_warning',
      severity: 'warning',
      pageId: page.id,
      photoId: photo.id,
      title: 'Fotka vypadá jako screenshot',
      message: `${photo.fileName} může být screenshot nebo snímek obrazovky.`,
      recommendation: 'Ověř tiskovou kvalitu, případně nahraď snímek originální fotografií.',
    })];
  });
  checks.push(result(definition('screenshot_warning'), project.pages.length - screenshotIssues.length, screenshotIssues));

  const aspectIssues = project.pages.flatMap((page) => {
    const photo = getPhoto(project, page);
    if (!photo || !hasUnusualAspectRatio(photo)) return [];
    return [issue({
      checkId: 'unusual_aspect_ratio',
      severity: 'warning',
      pageId: page.id,
      photoId: photo.id,
      title: 'Neobvyklý poměr stran',
      message: `${photo.fileName} má poměr stran, který může vyžadovat ruční ořez.`,
      recommendation: 'Zkontroluj kompozici v editoru a uprav crop tak, aby hlavní objekt zůstal v bezpečné zóně.',
    })];
  });
  checks.push(result(definition('unusual_aspect_ratio'), project.pages.length - aspectIssues.length, aspectIssues));

  const cropIssues = project.pages.flatMap((page) => {
    const crop = page.crop;
    if (!crop) return [];
    const isExtreme = crop.zoom > 2.2 || crop.x < 0.08 || crop.x > 0.92 || crop.y < 0.08 || crop.y > 0.92 || Math.abs(crop.rotation) > 4;
    if (!isExtreme) return [];
    return [issue({
      checkId: 'crop_extreme',
      severity: 'warning',
      pageId: page.id,
      title: 'Extrémní ořez fotografie',
      message: `${pageLabel(page)} má výrazný zoom, posun nebo rotaci.`,
      recommendation: 'Zkontroluj stránku v editoru, aby důležité části fotky nebyly mimo bezpečnou zónu.',
    })];
  });
  checks.push(result(definition('crop_extreme'), project.pages.length - cropIssues.length, cropIssues));

  const textIssues = project.pages.flatMap((page) => {
    const length = page.notes?.length ?? 0;
    if (length <= 80) return [];
    return [issue({
      checkId: 'text_length',
      severity: length <= 120 ? 'warning' : 'error',
      pageId: page.id,
      title: length <= 120 ? 'Delší popisek' : 'Příliš dlouhý popisek',
      message: `${pageLabel(page)} má popisek dlouhý ${length} znaků.`,
      recommendation: length <= 120 ? 'Zkrať popisek pro čistší sazbu.' : 'Zkrať popisek pod 120 znaků, ideálně do 80 znaků.',
    })];
  });
  checks.push(result(definition('text_length'), project.pages.length - textIssues.length, textIssues));

  const safeZoneIssues = project.pages.filter((page) => !page.crop || !page.layoutId).map((page) => issue({
    checkId: 'safe_zone',
    severity: 'warning',
    pageId: page.id,
    title: 'Neúplná data bezpečné zóny',
    message: `${pageLabel(page)} nemá kompletní crop/layout data pro MVP kontrolu bezpečné zóny.`,
    recommendation: 'Otevři stránku v editoru a ulož crop i layout.',
  }));
  checks.push(result(definition('safe_zone'), project.pages.length - safeZoneIssues.length, safeZoneIssues));

  const bleedIssues = project.pages.filter((page) => !page.layoutId).map((page) => issue({
    checkId: 'bleed',
    severity: 'warning',
    pageId: page.id,
    title: 'Chybí layout pro spadávku',
    message: `${pageLabel(page)} nemá vybraný layout.`,
    recommendation: 'Vyber layout stránky v editoru.',
  }));
  checks.push(result(definition('bleed'), project.pages.length - bleedIssues.length, bleedIssues));

  const localAssetIssues = project.pages.flatMap((page) => {
    const photo = getPhoto(project, page);
    const hasStoredAsset = photo ? availableLocalAssetPhotoIds?.has(photo.id) ?? photo.hasLocalAsset === true : false;
    if (!photo || hasStoredAsset || (photo.objectUrl && hasPhotoSessionUrl(photo.objectUrl))) return [];
    return [issue({
      checkId: 'local_assets',
      severity: 'warning',
      pageId: page.id,
      photoId: photo.id,
      title: 'Lokální asset chybí',
      message: 'Originály nejsou v tomto prohlížeči dostupné. Znovu nahraj fotky nebo použij budoucí cloud storage.',
      recommendation: 'Znovu nahraj fotky v tomto prohlížeči, nebo počkej na budoucí cloud storage kontrakt pro produkční assety.',
    })];
  });
  checks.push(result(definition('local_assets'), project.pages.length - localAssetIssues.length, localAssetIssues));

  const warningCount = checks.reduce((sum, check) => sum + check.warnings, 0);
  const errorCount = checks.reduce((sum, check) => sum + check.errors, 0);
  const okCount = checks.filter((check) => check.severity === 'ok').length;
  const overallStatus: PreflightSeverity = errorCount ? 'error' : warningCount ? 'warning' : 'ok';

  return {
    generatedAt: new Date().toISOString(),
    overallStatus,
    totalPages: project.pages.length,
    okCount,
    warningCount,
    errorCount,
    checks,
  };
}
